import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, extractError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import TicketTimeline from "@/components/TicketTimeline";
import { Sparkles, Loader2, Lock, Star, ArrowLeft, Bot, Server as ServerIcon, Radio } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

const STATUS_TONE = {
  open: "bg-primary/10 text-primary border-primary/30",
  in_progress: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  waiting_staff: "bg-primary/10 text-primary border-primary/30",
  waiting_customer: "bg-accent/10 text-accent border-accent/30",
  solved: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
  reopened: "bg-accent/10 text-accent border-accent/30",
};

const STATUSES = ["open", "in_progress", "waiting_customer", "waiting_staff", "solved", "closed", "reopened"];
const PRIORITIES = ["low", "normal", "high", "urgent", "critical"];

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isStaff = user?.role === "agent" || user?.role === "admin";
  const [ticket, setTicket] = useState(null);
  const [events, setEvents] = useState([]);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [agents, setAgents] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [rating, setRating] = useState(0);
  const [onlineIds, setOnlineIds] = useState([]);
  const [typingBy, setTypingBy] = useState(null);
  const wsRef = useRef(null);
  const typingTimerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setTicket(data.ticket);
      setEvents(data.events);
      setRating(data.ticket.rating || 0);
    } catch (e) { toast.error(extractError(e)); }
  }, [id]);

  useEffect(() => {
    load();
    if (isStaff) api.get("/admin/agents").then((r) => setAgents(r.data)).catch(() => {});
  }, [load, isStaff]);

  // WebSocket for live chat
  useEffect(() => {
    if (!id || !user) return;
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws/tickets/${id}`;
    let ws;
    try { ws = new WebSocket(wsUrl); } catch { return; }
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "presence") setOnlineIds(data.online || []);
        else if (data.type === "typing") {
          if (data.is_typing) setTypingBy(data.name);
          else setTypingBy(null);
        }
        else if (data.type === "message") {
          setEvents((prev) => [...prev, data.event]);
        }
        else if (data.type === "status") {
          setTicket((t) => t ? { ...t, status: data.status } : t);
        }
      } catch {}
    };
    ws.onclose = () => { wsRef.current = null; };
    return () => { try { ws.close(); } catch {} };
  }, [id, user]);

  const emitTyping = (isTyping) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "typing", is_typing: isTyping }));
  };
  const handleReplyChange = (v) => {
    setReply(v);
    emitTyping(v.length > 0);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => emitTyping(false), 1500);
  };

  const send = async () => {
    if (!reply.trim()) return;
    // Prefer WebSocket for real-time; fallback to REST
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ type: "message", body: reply, internal }));
      setReply(""); setInternal(false); emitTyping(false);
      return;
    }
    setSending(true);
    try {
      await api.post(`/tickets/${id}/reply`, { body: reply, internal });
      setReply(""); setInternal(false);
      await load();
      toast.success("Reply sent");
    } catch (e) { toast.error(extractError(e)); }
    finally { setSending(false); }
  };

  const update = async (patch) => {
    try {
      await api.patch(`/tickets/${id}`, patch);
      await load();
      toast.success("Ticket updated");
    } catch (e) { toast.error(extractError(e)); }
  };

  const runAi = async (kind) => {
    setAiLoading(true);
    try {
      if (kind === "summary") {
        const { data } = await api.post("/ai/summarize", { ticket_id: id });
        setAiSummary(data.summary);
      } else {
        const { data } = await api.post("/ai/suggest-reply", { ticket_id: id });
        setReply(data.reply);
        toast.success("Draft suggested");
      }
    } catch (e) { toast.error(extractError(e)); }
    finally { setAiLoading(false); }
  };

  const submitRating = async (v) => {
    setRating(v);
    try {
      await api.post(`/tickets/${id}/rate`, { rating: v });
      toast.success("Thanks for the feedback");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  if (!ticket) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const isOwner = ticket.owner_id === user?.user_id;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/tickets" className="hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> All tickets</Link>
        <span>·</span>
        <span className="font-mono uppercase tracking-widest">{ticket.ticket_number}</span>
      </div>

      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">{ticket.subject}</h1>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
            <span>Opened by <span className="text-foreground font-medium">{ticket.owner_name}</span></span>
            <span>·</span>
            <span>{format(new Date(ticket.created_at), "PPp")}</span>
            <span>·</span>
            <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1 rounded border ${STATUS_TONE[ticket.status]}`}>{ticket.status.replace(/_/g, " ")}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest" data-testid="ws-presence">
              <Radio className={`h-3 w-3 ${onlineIds.length > 1 ? "text-emerald-500 animate-pulse" : "text-muted-foreground"}`} />
              {onlineIds.length > 1 ? `${onlineIds.length} online` : "Live"}
            </span>
          </div>
        </div>
        {isStaff && (
          <div className="flex items-center gap-2">
            <Button data-testid="ai-summarize-btn" variant="outline" size="sm" onClick={() => runAi("summary")} disabled={aiLoading} className="gap-2">
              {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />} AI summary
            </Button>
          </div>
        )}
      </header>

      {aiSummary && (
        <div className="p-4 rounded-lg border border-primary/40 bg-primary/5">
          <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-2 flex items-center gap-2"><Bot className="h-3 w-3" /> AI summary</div>
          <div className="text-sm whitespace-pre-wrap leading-relaxed">{aiSummary}</div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr,320px] gap-6">
        {/* Left: description + timeline + reply */}
        <div className="space-y-6 min-w-0">
          <section className="p-5 rounded-lg border border-border/60 bg-card">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Description</div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{ticket.description}</div>
          </section>

          <section>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Activity timeline</div>
            <TicketTimeline events={events} />
          </section>

          {["closed"].indexOf(ticket.status) === -1 && (
            <section className="p-5 rounded-lg border border-border/60 bg-card space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Reply</div>
                <div className="flex items-center gap-2">
                  {isStaff && (
                    <>
                      <Button data-testid="ai-suggest-btn" variant="ghost" size="sm" onClick={() => runAi("reply")} disabled={aiLoading} className="gap-1.5">
                        {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Suggest
                      </Button>
                      <label className="text-xs flex items-center gap-1.5 cursor-pointer">
                        <input data-testid="internal-note-toggle" type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                        <Lock className="h-3 w-3" /> Internal note
                      </label>
                    </>
                  )}
                </div>
              </div>
              <Textarea data-testid="reply-input" value={reply} onChange={(e) => handleReplyChange(e.target.value)} rows={5} placeholder={internal ? "Only staff will see this…" : "Write your reply…"} />
              {typingBy && <div className="text-xs text-muted-foreground italic" data-testid="typing-indicator">{typingBy} is typing…</div>}
              <div className="flex justify-end">
                <Button data-testid="send-reply-btn" onClick={send} disabled={sending || !reply.trim()} className="gap-2">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reply"}
                </Button>
              </div>
            </section>
          )}

          {isOwner && ticket.status === "solved" && !ticket.rating && (
            <section className="p-5 rounded-lg border border-primary/40 bg-primary/5">
              <div className="text-sm mb-3">How was this experience?</div>
              <div className="flex items-center gap-1.5">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} onClick={() => submitRating(n)} data-testid={`rate-${n}`} className="p-1">
                    <Star className={`h-6 w-6 ${n <= rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right: metadata sidebar */}
        <aside className="space-y-4">
          <div className="p-4 rounded-lg border border-border/60 bg-card space-y-3 text-sm">
            <MetaRow label="Priority" value={
              isStaff ? (
                <Select value={ticket.priority} onValueChange={(v) => update({ priority: v })}>
                  <SelectTrigger className="h-8 w-full" data-testid="side-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              ) : <span className="font-mono uppercase tracking-widest text-xs">{ticket.priority}</span>
            } />
            <MetaRow label="Status" value={
              isStaff ? (
                <Select value={ticket.status} onValueChange={(v) => update({ status: v })}>
                  <SelectTrigger className="h-8 w-full" data-testid="side-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}</SelectContent>
                </Select>
              ) : <span className="font-mono uppercase tracking-widest text-xs">{ticket.status.replace(/_/g," ")}</span>
            } />
            <MetaRow label="Category" value={<span className="text-sm">{ticket.category}</span>} />
            <MetaRow label="Department" value={<span className="text-sm">{ticket.department}</span>} />
            {isStaff && (
              <MetaRow label="Assignee" value={
                <Select value={ticket.assignee_id || "unassigned"} onValueChange={(v) => update({ assignee_id: v === "unassigned" ? null : v })}>
                  <SelectTrigger className="h-8 w-full" data-testid="side-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {agents.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              } />
            )}
            {!isStaff && ticket.assignee_name && <MetaRow label="Assignee" value={ticket.assignee_name} />}
            {ticket.attached_server && (
              <MetaRow label="Server" value={
                <div className="flex items-center gap-1.5 text-sm">
                  <ServerIcon className="h-3.5 w-3.5 text-primary" />
                  <span className="font-medium">{ticket.attached_server.name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{ticket.attached_server.identifier}</span>
                </div>
              } />
            )}
            <MetaRow label="Created" value={formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })} />
            {ticket.first_response_at && <MetaRow label="First response" value={formatDistanceToNow(new Date(ticket.first_response_at), { addSuffix: true })} />}
            {ticket.resolved_at && <MetaRow label="Resolved" value={formatDistanceToNow(new Date(ticket.resolved_at), { addSuffix: true })} />}
            {ticket.rating && <MetaRow label="Rating" value={<span className="font-mono">{ticket.rating}★</span>} />}
          </div>
        </aside>
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground w-24 shrink-0">{label}</div>
      <div className="flex-1 min-w-0">{value}</div>
    </div>
  );
}
